import { cn } from '@r/utils/utils.ts';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { PasswordInput } from '@ui/password-input.tsx';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLogin } from '@r/api/auth.tsx';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../ui/form.tsx';
import { useTranslation } from 'react-i18next';
import { Link, useRouter } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@ui/alert.tsx';
import { useEffect } from 'react';
import { isOrvalError } from '@r/api/orval-error-helper.ts';

const BLOCKED_ACCOUNT_ERROR_MESSAGE = 'error.access_denied';
const INTERNAL_SERVER_ERROR_MESSAGE = 'Internal Server Error';

function getLoginErrorMessage(error: unknown, t: ReturnType<typeof useTranslation>['t']) {
  const response = isOrvalError(error) ? error.response : undefined;

  if (response?.status === 403 && response.data.message === BLOCKED_ACCOUNT_ERROR_MESSAGE) {
    return t(BLOCKED_ACCOUNT_ERROR_MESSAGE);
  }

  if ((error as { message?: string })?.message === INTERNAL_SERVER_ERROR_MESSAGE) {
    return INTERNAL_SERVER_ERROR_MESSAGE;
  }

  return t('error.auth.invalid.credentials');
}

type UserAuthFormProps = {
  showFirstLoginLink?: boolean;
};

export const UserAuthForm = ({ showFirstLoginLink = false }: UserAuthFormProps) => {
  const router = useRouter();
  const login = useLogin();

  function onSubmit(data: z.infer<typeof loginSchema>) {
    login.mutate(data);
  }

  useEffect(() => {
    if (login.isSuccess) {
      router.invalidate();
    }
  }, [login.isSuccess]);

  const { t } = useTranslation();

  const loginSchema = z.object({
    email: z.string().min(1, t('form.fields.required')).email(t('form.field.invalid')),
    password: z.string().min(1, t('form.fields.required'))
  });

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  return (
    <div className={cn('space-y-8')}>
      {login.error ? (
        <Alert variant="destructive" className="mb-10" data-testid="error-message">
          <AlertCircle className="w-4 h-4" />
          <AlertTitle>{t('error.label')}</AlertTitle>
          <AlertDescription>{getLoginErrorMessage(login.error, t)}</AlertDescription>
        </Alert>
      ) : null}
      <Form {...form}>
        <form data-testid="login-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <FormField
            control={form.control}
            name="email"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.email.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('auth.email.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('auth.password.label')}</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder={t('auth.password.placeholder')}
                    {...field}
                    error={!!fieldState.error}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-between">
            <p className="text-sm text-muted-foreground">
              <Link
                to="/reset-password"
                className="font-light underline underline-offset-4 hover:text-foreground"
              >
                {t('auth.password.forgot')}
              </Link>
            </p>

            {showFirstLoginLink ? (
              <p className="text-sm text-muted-foreground">
                <Link
                  to={'/first-login' as '/login'}
                  className="font-light underline underline-offset-4 hover:text-foreground"
                >
                  {t('auth.first.login')}
                </Link>
              </p>
            ) : null}
          </div>
          <Button className="w-full" type="submit" disabled={login.isPending}>
            {login.isPending ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                {t('auth.btn.submit')}
              </>
            ) : (
              t('auth.btn.submit')
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};
